const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000
const UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term', 'id'] as const
const CLICK_KEYS = ['fbclid', 'gclid', 'ttclid', 'msclkid'] as const

export type StoredUtm = {
  source?: string
  medium?: string
  campaign?: string
  content?: string
  term?: string
  id?: string
}

export type StoredClickIds = Partial<Record<(typeof CLICK_KEYS)[number], string>>

function storageKey(campaignId: number): string {
  return `legalprime:campaignAttribution:${campaignId}`
}

function cleanAttributionValue(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.replace(/[\u0000-\u001F\u007F]/g, '').trim()
  if (!trimmed || trimmed.length > 255 || /[<>]/.test(trimmed)) return null
  return trimmed
}

function bundleFrom(utm: unknown, clickIds: unknown): { utm: StoredUtm; clickIds: StoredClickIds } {
  const source = utm && typeof utm === 'object' ? (utm as Record<string, unknown>) : {}
  const clicks = clickIds && typeof clickIds === 'object' ? (clickIds as Record<string, unknown>) : {}
  const cleanUtm: StoredUtm = {}
  const cleanClicks: StoredClickIds = {}

  for (const key of UTM_KEYS) {
    const value = cleanAttributionValue(source[key])
    if (value) cleanUtm[key] = value
  }
  for (const key of CLICK_KEYS) {
    const value = cleanAttributionValue(clicks[key])
    if (value) cleanClicks[key] = value
  }

  return { utm: cleanUtm, clickIds: cleanClicks }
}

function hasAttribution(bundle: { utm: StoredUtm; clickIds: StoredClickIds }): boolean {
  return Object.keys(bundle.utm).length > 0 || Object.keys(bundle.clickIds).length > 0
}

function readStoredAttribution(campaignId: number): { utm: StoredUtm; clickIds: StoredClickIds } | null {
  const raw = window.localStorage.getItem(storageKey(campaignId))
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as { saved_at?: unknown; utm?: unknown; click_ids?: unknown }
    const savedAt = typeof parsed.saved_at === 'number' ? parsed.saved_at : Number.NaN
    if (!Number.isFinite(savedAt) || Date.now() - savedAt > ATTRIBUTION_TTL_MS) {
      window.localStorage.removeItem(storageKey(campaignId))
      return null
    }
    const bundle = bundleFrom(parsed.utm, parsed.click_ids)
    return hasAttribution(bundle) ? bundle : null
  } catch {
    window.localStorage.removeItem(storageKey(campaignId))
    return null
  }
}

function writeStoredAttribution(
  campaignId: number,
  bundle: { utm: StoredUtm; clickIds: StoredClickIds }
): void {
  window.localStorage.setItem(
    storageKey(campaignId),
    JSON.stringify({
      saved_at: Date.now(),
      utm: bundle.utm,
      click_ids: bundle.clickIds,
    })
  )
}

export function rememberAttribution(
  campaignId: number,
  utm: StoredUtm | undefined,
  clickIds: Record<string, string> | undefined
): { utm: StoredUtm | undefined; clickIds: Record<string, string> | undefined } {
  if (typeof window === 'undefined' || !Number.isInteger(campaignId) || campaignId < 1) {
    return { utm, clickIds }
  }

  const current = bundleFrom(utm, clickIds)
  if (hasAttribution(current)) {
    writeStoredAttribution(campaignId, current)
    return {
      utm: Object.keys(current.utm).length ? current.utm : undefined,
      clickIds: Object.keys(current.clickIds).length ? current.clickIds : undefined,
    }
  }

  const stored = readStoredAttribution(campaignId)
  if (!stored) {
    return { utm: undefined, clickIds: undefined }
  }

  return {
    utm: Object.keys(stored.utm).length ? stored.utm : undefined,
    clickIds: Object.keys(stored.clickIds).length ? stored.clickIds : undefined,
  }
}
